/**
 * Ecom link wiring — resolves each matching anchor's `ClickoutPayload` when
 * `ecom.init` runs and binds that anchor a listener over the resolved value, so
 * a click only sends the analytics. The browser follows the href.
 *
 * Anchors added after init stay unwired until the next `ecom.init`.
 */

import type { SdkContext } from '../types';
import { dispatchEcomClickout } from './clickout';
import type { ClickoutPayload } from './events';
import {
  destinationFromImpactUrl,
  isWalmartImpactUrl,
  isWalmartUrl,
} from './walmart';

export const DEFAULT_ECOM_LINK_SELECTOR = 'a[href*="amazon."], a[href*="walmart.com"]';

export interface WireEcomLinksOptions {
  /** CSS selector for affiliate anchors within root. Default Amazon + Walmart. */
  linkSelector?: string;
  /**
   * Amazon associate tag fallback. When omitted, `ecom.init` uses
   * `config.ecom.amazonDefaultTag` (`ecom_amazon_default_tag` setting).
   */
  defaultTag?: string;
  /**
   * When true (default), enforce config `ecom_tags_prefix` on `?ch=`.
   * Pass `false` to skip the check (yoto `ecmtrtg_man` parity).
   */
  enforceTagsPrefix?: boolean;
}

const AMAZON_ASIN_PATTERN = /\/(?:dp|gp\/product|gp\/aw\/d)\/([A-Z0-9]{10})(?:[/?]|$)/i;

/**
 * Items that already sent a clickout this page load, keyed by `postItemId` when
 * present and the outbound URL otherwise. Repeat clicks send nothing (yoto
 * `ITEMS_CLICKED` parity).
 */
const clickedItems = new Set<string>();

/**
 * `epm` for this page, resolved from the post / website config at `ecom.init`.
 * A `data-kz-market` attribute on the anchor overrides it.
 */
let ecomProductMarket = '';

export function setEcomProductMarket(market: string): void {
  ecomProductMarket = market;
}

export function _resetEcomLinksForTests(): void {
  clickedItems.clear();
  ecomProductMarket = '';
}

/** Amazon ASIN from a product-detail URL, or null for general links. */
export function extractAsinFromUrl(url: string): string | null {
  const asin = AMAZON_ASIN_PATTERN.exec(url)?.[1];
  return asin ? asin.toUpperCase() : null;
}

function numAttr(anchor: HTMLAnchorElement, name: string): number | undefined {
  const raw = anchor.getAttribute(name);
  if (raw === null || raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/** `/dye` params that cannot be derived from the URL. Authored on the anchor. */
function itemContextFromAnchor(anchor: HTMLAnchorElement): Partial<ClickoutPayload> {
  const postId = anchor.getAttribute('data-kz-pid');
  const postItemId = anchor.getAttribute('data-kz-iid');
  const itemPageIndex = numAttr(anchor, 'data-kz-ipi');
  const itemArticleIndex = numAttr(anchor, 'data-kz-iai');
  const pageNumber = numAttr(anchor, 'data-kz-pn');

  return {
    ...(postId ? { postId } : {}),
    ...(postItemId ? { postItemId } : {}),
    ...(itemPageIndex !== undefined ? { itemPageIndex } : {}),
    ...(itemArticleIndex !== undefined ? { itemArticleIndex } : {}),
    ...(pageNumber !== undefined ? { pageNumber } : {}),
  };
}

function clickoutPayloadFromAnchor(anchor: HTMLAnchorElement): ClickoutPayload | null {
  const href = anchor.getAttribute('href');
  if (!href || href === '#') return null;

  const ecsid = anchor.getAttribute('data-kz-ecsid');
  const ecptg = anchor.getAttribute('data-kz-ecptg');
  const eatag = anchor.getAttribute('data-kz-eatag');
  const eataf = anchor.getAttribute('data-kz-eataf');
  const eataid = anchor.getAttribute('data-kz-eataid');
  const eatafr = anchor.getAttribute('data-kz-eatafr');
  const eatas = anchor.getAttribute('data-kz-eatas');
  const epm = anchor.getAttribute('data-kz-epm');
  const dest = anchor.getAttribute('data-kz-dest');
  const market = anchor.getAttribute('data-kz-market') || ecomProductMarket;
  const context = itemContextFromAnchor(anchor);

  // Walmart Impact clickout
  if (epm === 'walmart' || isWalmartImpactUrl(href) || isWalmartUrl(href)) {
    const destination =
      dest ||
      destinationFromImpactUrl(href) ||
      href;
    return {
      url: href,
      advertiserCampaignId: destination,
      ecomProductMarket: 'walmart',
      ...(ecsid ? { ecsid } : {}),
      ...context,
      extras: { acm: 'E2' },
    };
  }

  // Any other selector match is an affiliate link the caller declared; only
  // Amazon carries an ASIN.
  //
  // Per the ClickoutPayload contract: `advertiserCampaignId` is the ASIN for
  // product-detail links, or the full URL for general links — never the
  // subtag/click id (`ecsid`).
  const asin = href.indexOf('amazon.') === -1 ? null : extractAsinFromUrl(href);

  return {
    url: href,
    advertiserCampaignId: asin ?? href,
    ...(asin ? { productId: asin } : {}),
    ...(market ? { ecomProductMarket: market } : {}),
    ...(ecsid ? { ecsid } : {}),
    ...(ecptg ? { ecptg } : {}),
    ...context,
    ...(eatag ? {
      extras: {
        eatag,
        ...(eataf ? { eataf } : {}),
        ...(eataid ? { eataid } : {}),
        ...(eatafr ? { eatafr } : {}),
        ...(eatas ? { eatas } : {}),
      },
    } : {}),
  };
}

export function wireEcomLinks(
  ctx: SdkContext,
  root: Element | Document,
  opts: WireEcomLinksOptions,
): () => void {
  const selector = opts.linkSelector ?? DEFAULT_ECOM_LINK_SELECTOR;
  const bound: Array<{ anchor: HTMLAnchorElement; listener: EventListener }> = [];

  for (const anchor of root.querySelectorAll<HTMLAnchorElement>(selector)) {
    const payload = clickoutPayloadFromAnchor(anchor);
    if (!payload) continue;

    const dedupeKey = payload.postItemId ?? payload.url;
    const listener = () => {
      if (clickedItems.has(dedupeKey)) return;
      clickedItems.add(dedupeKey);
      dispatchEcomClickout(ctx, payload, { navigate: false });
    };

    anchor.addEventListener('click', listener);
    bound.push({ anchor, listener });
  }

  return () => {
    for (const { anchor, listener } of bound) {
      anchor.removeEventListener('click', listener);
    }
    bound.length = 0;
  };
}
